import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify';
import { Siren, Users, HeartPulse, MessagesSquare, Hourglass } from 'lucide-react';

import Header from '../components/Dashboard/Header'
import Sidebar from '../layouts/Sidebar'
import PieCharts from '../components/Charts/PieChart'
import DashboardMap from '../components/Dashboard/DashboardMap';
import ConsumptionChart from '../components/Charts/ConsumptionChart';
import AnnouncementsList from '../components/Dashboard/AnnouncementsList';
import Card, { CardHeader } from '../components/ui/Card';
import KpiCard from '../components/ui/KpiCard';
import { PageLoader } from '../components/ui/Spinner';

import AnnouncementService from '../services/AnnouncementService';
import DashboardService from '../services/DashboardService';

const teamSeries = [
    { dataKey: 'active', label: "Active" },
    { dataKey: 'partiallyActive', label: "Partially Active" },
    { dataKey: 'inActive', label: "Inactive" },
]

const CBRNE_TYPES = [
    { key: 'Chemical', label: 'C', dot: 'bg-red-600' },
    { key: 'Biological', label: 'B', dot: 'bg-orange-600' },
    { key: 'Radiological', label: 'R', dot: 'bg-green-600' },
    { key: 'Nuclear', label: 'N', dot: 'bg-purple-600' },
    { key: 'Explosion', label: 'E', dot: 'bg-neutral-800' },
];

// 'live' hides Closed/Rejected by default — the map and incident feed are meant
// to reflect what's currently happening, not the full historical log (that's
// what the /activation list and Reports pages are for).
const STATUS_PILLS = [
    { value: 'live', label: 'Live' },
    { value: 'all', label: 'All Statuses' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Active', label: 'Active' },
    { value: 'Closed', label: 'Closed' },
];
const LIVE_STATUSES = ['Pending', 'Active'];

function Dashboard() {

    const [announcements, setAnnouncements] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('live');

    const loadAnnouncements = async () => {
        try {
            let response = await AnnouncementService.findAll(1, 100);
            if (response?.status === 200) {
                setAnnouncements(response.data)
                if (response.data.length > announcements.length)
                    toast.info("New announcement:" + response.data[response.data.length - 1].title)
            }
        } catch (error) {
            console.log(error);
        }
    };

    const loadStats = async () => {
        try {
            const response = await DashboardService.getStats();
            if (response?.status === 200) {
                setStats(response.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        Promise.all([loadAnnouncements(), loadStats()]).finally(() => setLoading(false));

        const intervalId = setInterval(() => {
            loadAnnouncements();
            loadStats();
        }, 30000);

        return () => clearInterval(intervalId);
    }, []);

    const visibleAnnouncements = useMemo(() => {
        if (statusFilter === 'all') return announcements;
        if (statusFilter === 'live') {
            return announcements.filter((a) => LIVE_STATUSES.includes(a.status || 'Pending'));
        }
        return announcements.filter((a) => (a.status || 'Pending') === statusFilter);
    }, [announcements, statusFilter]);

    const incidentsByType = stats?.incidentsByType || {};
    const drillsByType = stats?.drillsByType || {};
    const casualties = stats?.casualties || { total: 0, green: 0, yellow: 0, red: 0 };
    const teamDataset = stats?.teamDataset || [];
    const responses = stats?.responses || { accepted: 0, rejected: 0, pending: 0 };

    const patientData = [
        { value: casualties.green, label: 'Green' },
        { value: casualties.yellow, label: 'Yellow' },
        { value: casualties.red, label: 'Red' },
    ].filter(d => d.value > 0);

    const totalCasualties = casualties.total;
    const totalResponses = responses.accepted + responses.rejected + responses.pending;

    if (loading) {
        return (
            <Sidebar title="Admin Dashboard" header={<Header />}>
                <PageLoader label="Loading dashboard…" />
            </Sidebar>
        );
    }

    return (
        <Sidebar title="Admin Dashboard" header={<Header />}>
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                    <KpiCard
                        label="Incidents & Drills"
                        value={stats?.activeIncidents ?? 0}
                        valueLabel="Active Incidents"
                        valueTotal={`${stats?.totalIncidents ?? 0} total`}
                        valueColor="text-red-600 dark:text-red-400"
                        secondaryValue={stats?.activeDrills ?? 0}
                        secondaryValueLabel="Active Drills"
                        secondaryValueTotal={`${stats?.totalDrills ?? 0} total`}
                        secondaryValueColor="text-blue-600 dark:text-blue-400"
                        note={`${stats?.totalAnnouncements ?? 0} total announcements (incidents & drills combined)`}
                        icon={Siren}
                        type="red"
                    />
                    <KpiCard
                        label="Teams Mobilized"
                        value={stats?.totalTeams ?? 0}
                        valueColor="text-green-600 dark:text-green-400"
                        note={`${stats?.totalMembers ?? 0} members enrolled`}
                        icon={Users}
                        type="green"
                    />
                    <KpiCard
                        label="Casualties Logged"
                        value={totalCasualties}
                        note={(
                            <>
                                <span className="text-red-600 dark:text-red-400 font-semibold">{casualties.red} red</span>
                                {' • '}
                                <span className="text-orange-600 dark:text-orange-400 font-semibold">{casualties.yellow} yellow</span>
                                {' • '}
                                <span className="text-green-600 dark:text-green-400 font-semibold">{casualties.green} green</span>
                            </>
                        )}
                        icon={HeartPulse}
                        type="blue"
                    />
                    <KpiCard
                        label="Member Response"
                        value={`${responses.accepted}/${totalResponses}`}
                        note={(
                            <>
                                <span className="text-orange-600 dark:text-orange-400 font-semibold">{responses.pending} pending</span>
                                {' • '}
                                <span className="text-red-600 dark:text-red-400 font-semibold">{responses.rejected} rejected</span>
                            </>
                        )}
                        icon={MessagesSquare}
                        type="amber"
                    />
                    <KpiCard
                        label="Pending Approval"
                        value={stats?.pendingIncidents ?? 0}
                        valueColor="text-orange-600 dark:text-orange-400"
                        note={`${stats?.closedIncidents ?? 0} closed (all-time)`}
                        icon={Hourglass}
                    />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="font-bold text-neutral-800 text-sm dark:text-neutral-100">
                        Live Operations
                        <span className="font-medium text-neutral-400 dark:text-neutral-500"> · {visibleAnnouncements.length} shown</span>
                    </h3>
                    <div className="flex p-1 bg-white border border-neutral-200 rounded-lg shadow-sm dark:bg-ink-900 dark:border-neutral-700">
                        {STATUS_PILLS.map((pill) => (
                            <button
                                key={pill.value}
                                type="button"
                                onClick={() => setStatusFilter(pill.value)}
                                className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                                    statusFilter === pill.value
                                        ? 'bg-seha-50 text-seha-700 shadow-sm dark:bg-seha-500/10 dark:text-seha-300'
                                        : 'text-neutral-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-ink-800'
                                }`}
                            >
                                {pill.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-2">
                        <DashboardMap announcements={visibleAnnouncements} height="520px" />
                    </div>

                    <Card padded={false} className="flex flex-col overflow-hidden" style={{ minHeight: 520 }}>
                        <CardHeader title="Incident Navigation" subtitle={`${visibleAnnouncements.length} shown`} />
                        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 464 }}>
                            <AnnouncementsList announcements={visibleAnnouncements} />
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <Card>
                        <h3 className="font-bold text-neutral-800 text-sm mb-4 dark:text-neutral-100">CBRNE Breakdown</h3>
                        <div className="space-y-5">
                            <div>
                                <p className="kpi-label mb-3">Incidents ({stats?.totalIncidents ?? 0})</p>
                                <div className="flex items-center justify-between">
                                    {CBRNE_TYPES.map((t) => (
                                        <div key={t.key} className="flex flex-col items-center gap-1.5">
                                            <span className={`w-7 h-7 rounded-full ${t.dot} text-white text-xs font-bold flex items-center justify-center`}>
                                                {t.label}
                                            </span>
                                            <span className="text-sm font-bold text-neutral-800 dark:text-neutral-100">{incidentsByType[t.key] || 0}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="pt-4 border-t border-neutral-100 dark:border-neutral-800">
                                <p className="kpi-label mb-3">Drills ({stats?.totalDrills ?? 0})</p>
                                <div className="flex items-center justify-between">
                                    {CBRNE_TYPES.map((t) => (
                                        <div key={t.key} className="flex flex-col items-center gap-1.5">
                                            <span className={`w-7 h-7 rounded-full ${t.dot} text-white text-xs font-bold flex items-center justify-center`}>
                                                {t.label}
                                            </span>
                                            <span className="text-sm font-bold text-neutral-800 dark:text-neutral-100">{drillsByType[t.key] || 0}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <PieCharts
                            label="Casualties"
                            data={patientData.length > 0 ? patientData : [{ value: 1, label: 'None logged' }]}
                            height={200}
                        />
                        <p className="kpi-note mt-2">
                            {casualties.red} red • {casualties.yellow} yellow • {casualties.green} green
                        </p>
                    </Card>

                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-neutral-800 text-sm dark:text-neutral-100">Team Response</h3>
                            <span className="text-xs text-neutral-400 font-medium dark:text-neutral-500">
                                {stats?.totalMembers ?? 0} members • {stats?.totalTeams ?? 0} teams
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-4 mb-4 text-xs font-semibold">
                            <span className="flex items-center gap-1.5 text-green-700 dark:text-green-400">
                                <span className="w-2 h-2 rounded-full bg-green-600" />
                                Accepted {responses.accepted}
                            </span>
                            <span className="flex items-center gap-1.5 text-red-700 dark:text-red-400">
                                <span className="w-2 h-2 rounded-full bg-red-600" />
                                Rejected {responses.rejected}
                            </span>
                            <span className="flex items-center gap-1.5 text-orange-700 dark:text-orange-400">
                                <span className="w-2 h-2 rounded-full bg-orange-600" />
                                Pending {responses.pending}
                            </span>
                        </div>
                        {teamDataset.length > 0 ? (
                            <div className="overflow-x-auto">
                                <ConsumptionChart
                                    data={teamDataset}
                                    series={teamSeries}
                                    height={200}
                                    width={Math.max(320, teamDataset.length * 90)}
                                />
                            </div>
                        ) : (
                            <div className="text-sm text-neutral-400 text-center py-10 dark:text-neutral-500">No team data yet</div>
                        )}
                    </Card>
                </div>
            </div>
        </Sidebar>
    )
}

export default Dashboard
